import type {
    Aggregation,
    AggregationOption,
    CategoryTree,
    FilterTypeInput,
    Maybe,
    ProductAttributeSortInput
} from '@pylot-data/pylotschema'
import type { SortEnum } from '@pylot-data/enums/SortEnum.d'
import type { CurrencyEnum } from '@pylot-data/enums/CurrencyEnum.d'
import { Dispatch, SetStateAction } from 'react'
import type { SearchCriteria } from './hooks/use-plp-manager'

type SubCategoriesProps = {
    category: CategoryTree
    categoryUrlSuffix?: string
    applyFilter: ApplyFilter
}

type FilterWrapperProps = {
    className?: string
    subCategories?: Maybe<JSX.Element> | false
    header?: Maybe<string>
    filtersApplied: number
    applyFilter: ApplyFilter
    filtersVisible: boolean
    setFiltersVisible: Dispatch<SetStateAction<boolean>>
    isAccordion?: boolean
    filtersList: Array<JSX.Element | undefined>
    plpState: PLPState
    appliedFilterList: Array<JSX.Element | undefined>
}

export enum ActionType {
    CLEAR_PLP_STATE = 'CLEAR_PLP_STATE',
    SET_CATEGORY_UID = 'SET_CATEGORY_UID',
    SET_FILTER = 'SET_FILTER',
    SET_AGGREGATIONS = 'SET_AGGREGATIONS',
    SORT = 'SORT',
    SET_FILTERS_VISIBLE = 'SET_FILTERS_VISIBLE',
    SET_SEARCH_CRITERIA = 'SET_SEARCH_CRITERIA'
}

type Action =
    | {
          type: ActionType.CLEAR_PLP_STATE
          payload: Partial<PLPState>
      }
    | {
          type: ActionType.SET_CATEGORY_UID
          payload: string
      }
    | {
          type: ActionType.SET_FILTER
          payload: FiltersStateInput
      }
    | {
          type: ActionType.SET_AGGREGATIONS
          payload: Array<Maybe<Aggregation>>
      }
    | {
          type: ActionType.SORT
          payload: ProductAttributeSortInput
      }
    | {
          type: ActionType.SET_FILTERS_VISIBLE
          payload: boolean
      }
    | {
          type: ActionType.SET_SEARCH_CRITERIA
          payload: SearchCriteria[]
      }

export type FilterListType = {
    [key: string]: FilterTypeInput
}

export type FiltersStateInput = {
    filters: Maybe<FilterListType>
    appliedFilterData: Array<FilterInputData>
}

export type PLPState = {
    categoryUid: string
    aggregations: Maybe<Array<Maybe<Aggregation>>>
    filters: FilterListType
    appliedFilterData: Array<FilterInputData>
    sort:
        | ProductAttributeSortInput
        | Record<string, SortEnum | null | undefined>
    filtersVisible: boolean
    appliedSearchCritieria: SearchCriteria[]
}

export type PLPManager = {
    plpState: PLPState
    clearPLPState: Dispatch<SetStateAction<Partial<PLPState>>>
    setCategoryUid: Dispatch<SetStateAction<string>>
    setFilter: (state: FiltersStateInput) => void
    setAggregations: Dispatch<SetStateAction<Array<Maybe<Aggregation>>>>
    applySort: Dispatch<SetStateAction<ProductAttributeSortInput>>
    setFiltersVisible: Dispatch<SetStateAction<boolean>>
    setSearchCriteria: Dispatch<SetStateAction<SearchCriteria[]>>
}

export type FiltersProps = {
    className?: string
    aggregations?: Maybe<Array<Maybe<Aggregation>>>
    id?: Maybe<number>
    excludeFilter?: string[]
    category?: Maybe<CategoryTree>
    isAccordion?: boolean
    header?: Maybe<string>
    showCategory?: boolean
    showCount?: boolean
    currencyCode?: Maybe<CurrencyEnum>
    isLoading?: boolean
    plpManager: PLPManager
    isHorizontal?: boolean
    iconOpen?: JSX.Element | null | false
    iconClose?: JSX.Element | null | false
    showFilterCount?: boolean
}

export type FilterInputData = {
    requestVar: string
    value_string: string
    label?: Maybe<string>
}

export type ApplyFilter = (
    filterInputData?: FilterInputData,
    isApplied?: boolean,
    isSingleSelect?: boolean
) => void

type FilterBlockProps = {
    items?: Maybe<Array<Maybe<AggregationOption>>>
    availableValues?: Maybe<Array<string>>
    name?: Maybe<string>
    index: number
    isHorizontal: boolean // Filters are not collapsible for isHorizontal=true
    applyFilter: ApplyFilter
    requestVar: string
    isLoading?: boolean
    showCount?: boolean
    currencyCode?: Maybe<string>
    appliedSearchCritieria: SearchCriteria[]
    iconOpen?: JSX.Element | null | false
    iconClose?: JSX.Element | null | false
    appliedFilterData: Array<FilterInputData>
    showFilterCount?: boolean
}

type FilterListProps = {
    items: Array<Maybe<AggregationOption>>
    availableValues?: Maybe<Array<string>>
    requestVar: string
    applyFilter: ApplyFilter
    isLoading?: boolean
    showCount?: boolean
    currencyCode?: Maybe<string>
    appliedSearchCritieria: SearchCriteria[]
    showMoreSize?: number // For isHorizontal=true we do not use 'See More'/'See Less'
    isHorizontal?: boolean
    appliedFilterData: Array<FilterInputData>
}
